<template>
  <div class="container mx-auto px-4 py-6">
    <div class="flex items-center justify-between mb-4 gap-4">
      <router-link
        to="/market-place"
        class="bg-[#0930bf] hover:bg-primary text-white font-bold py-1.5 px-3 rounded-lg inline-block text-xs flex-shrink-0"
      >
        ← Back to Marketplace
      </router-link>

      <button
        @click="isInCompare ? goToCompare() : addVehicleToCompare()"
        class="bg-[#0930bf] hover:bg-primary text-white font-bold py-1.5 px-3 rounded-lg inline-block text-xs flex-shrink-0"
      >
        {{ isInCompare ? "View Compare" : "+ Compare" }}
      </button>
    </div>

    <div v-if="loading" class="text-center py-8">
      Loading vehicle details...
    </div>

    <div
      v-else-if="vehicle"
      class="vehicle-details-page flex flex-col lg:flex-row gap-8"
    >
      <div class="lg:w-[50%] w-full flex justify-center items-center">
        <img
          :src="vehicle.image_url || placeholderImage"
          :alt="vehicle.specs.brand + ' ' + vehicle.specs.model"
          class="w-full max-w-md mx-auto mb-4"
        />
      </div>

      <div class="lg:w-[50%] w-full text-left">
        <p
          class="text-[18px] lg:text-[20px] text-gray-700 font-semibold lg:text-blue-900 mb-2"
        >
          {{ vehicle.specs.year }} {{ vehicle.specs.brand }}
          {{ vehicle.specs.model }}
        </p>

        <div
          class="flex flex-col lg:flex-col gap-0 initiation lg:gap-2 mb-2 items-baseline"
        >
          <p class="text-primary text-3xl font-extrabold">
            {{ vehiclePrice }}
          </p>
          <span class="text-[15px] text-primary">
            Initiation Fee: {{ formatCurrency(vehicle.initiation_fee) }}
          </span>
        </div>

        <div class="w-64">
          <h3 class="font-semibold mb-3 text-blue-900">Summary</h3>
          <ul class="text-sm mb-2 list-none pl-0">
            <li class="flex justify-between py-1">
              <div class="flex gap-1 items-center">
                <PhUmbrellaSimple /> <span>Insurance</span>
              </div>
              <span class="font-medium text-gray-700">
                <span class="font-medium text-gray-700">Included</span>
                <!-- {{
                formatCurrency(vehicle.specs.insurance)
              }} -->
              </span>
            </li>
            <li class="flex justify-between py-1">
              <div class="flex gap-1 items-center">
                <PhGpsFix /> <span>Tracking</span>
              </div>
              <span class="font-medium text-gray-700">
                <span class="font-medium text-gray-700">Included</span>
                <!-- {{ formatCurrency(vehicle.tracker) }} -->
              </span>
            </li>
            <li class="flex justify-between py-1">
              <div class="flex gap-1 items-center">
                <PhShieldCheckered /> <span>Warranty</span>
              </div>
              <span class="font-medium text-gray-700">Included</span>
            </li>
            <li class="flex justify-between py-1">
              <div class="flex gap-1 items-center">
                <PhWrench /> <span>Services</span>
              </div>
              <span class="font-medium text-gray-700">Included</span>
            </li>
            <li class="flex justify-between py-1">
              <div class="flex gap-1 items-center">
                <PhPoliceCar /> <span>Roadside Assistance</span>
              </div>
              <span class="font-medium text-gray-700">Included</span>
            </li>
            <li class="flex justify-between py-1">
              <div class="flex gap-1 items-center">
                <PhSpeedometer /> <span>Mileage</span>
              </div>
              <span class="font-medium text-gray-700">1,500 km</span>
            </li>
          </ul>
        </div>

        <!-- Specifications -->
        <div class="mb-6 rounded-lg">
          <h3 class="font-semibold mb-3 text-blue-900">Specifications</h3>
          <div class="grid grid-cols-2 gap-2 text-sm">
            <div class="font-medium">Available in:</div>
            <div class="flex gap-2 flex-wrap">
              <div
                v-for="(color, index) in colorOptions"
                :key="index"
                class="relative group"
              >
                <!-- Color circle -->
                <span
                  class="w-3 h-3 rounded-full border border-black block"
                  :style="{ backgroundColor: color.trim().toLowerCase() }"
                ></span>

                <!-- Tooltip -->
                <span
                  class="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-xs text-white bg-primary rounded opacity-0 group-hover:opacity-100 transition"
                >
                  {{ color }}
                </span>
              </div>
            </div>

            <div class="font-medium">Body Style:</div>
            <div>{{ vehicle.specs.body_type }}</div>

            <div class="font-medium">Transmission:</div>
            <div>{{ vehicle.specs.transmission }}</div>

            <div class="font-medium">Doors:</div>
            <div>{{ vehicle.specs.doors || "N/A" }}</div>

            <div class="font-medium">Drive Train:</div>
            <div>{{ vehicle.specs.drive_train }}</div>

            <div class="font-medium">Fuel Consumption:</div>
            <div>{{ vehicle.specs.fuel_consumption }}</div>
          </div>
        </div>

        <hr class="my-6 border-gray-200" />

        <a
          :href="`/apply-now?brand=${vehicle.specs.brand}&model=${vehicle.specs.model}`"
          class="bg-[#0930bf] hover:bg-primary text-white font-bold py-3 px-6 rounded-lg w-full mb-6 text-center block"
        >
          Apply Now
        </a>
      </div>
    </div>

    <div v-else class="text-center py-8">Vehicle not found</div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from "vue";
import { fetchMarketPlace } from "@/reusable/fetchMarketPlace";
import { toggleCompare, getCompareCars } from "@/utils/compareState";
import { formatCurrency } from "@/reusable/convertor";
import { useStore } from "vuex";
import {
  PhShieldCheckered,
  PhWrench,
  PhPoliceCar,
  PhSpeedometer,
} from "@phosphor-icons/vue";

export default {
  components: {
    PhShieldCheckered,
    PhWrench,
    PhPoliceCar,
    PhSpeedometer,
  },
  setup() {
    const store = useStore();
    const vehicle = ref(null);
    const loading = ref(true);
    const placeholderImage = ref("path_to_placeholder_image");

    const vehicleId = computed(() => store.getters["GET_VEHICLE_ID"]);

    const colorOptions = computed(() => {
      try {
        return vehicle.value.colours ? JSON.parse(vehicle.value.colours) : [];
      } catch (e) {
        return [];
      }
    });

    const compareCars = getCompareCars();

    const isInCompare = computed(() =>
      vehicle.value
        ? compareCars.value.some((c) => c.id === vehicleId.value)
        : false
    );

    onMounted(async () => {
      try {
        const data = await fetchMarketPlace();
        const foundVehicle = data.data.find(
          (item) => item.id === vehicleId.value && item.isDeleted === "0"
        );

        if (foundVehicle) {
          // Map specs like CarList
          vehicle.value = {
            ...foundVehicle,
            specs: {
              brand: foundVehicle.make,
              model: foundVehicle.model,
              year: foundVehicle.year,
              color: foundVehicle.colours
                ? JSON.parse(foundVehicle.colours)[0]
                : "Unknown",
              transmission: foundVehicle.transmission,
              price: parseFloat(foundVehicle.rental),
              body_type: foundVehicle.body_type,
              drive_train: foundVehicle.drive_train,
              fuel_consumption: foundVehicle.fuel_consumption,
              insurance: parseFloat(foundVehicle.insurance),
              tracker: parseFloat(foundVehicle.tracker),
              initiation_fee: parseFloat(foundVehicle.initiation_fee || 0),
              doors: foundVehicle.doors || null,
            },
          };
        }
      } catch (error) {
        console.error("Error fetching vehicle details:", error);
      } finally {
        loading.value = false;
      }
    });

    const vehiclePrice = computed(() => {
      if (!vehicle.value) return "R0";
      const price =
        parseFloat(vehicle.value.rental) +
        parseFloat(vehicle.value.insurance) +
        parseFloat(vehicle.value.tracker || 200);
      return formatCurrency(price);
    });

    return {
      vehicle,
      loading,
      colorOptions,
      placeholderImage,
      formatCurrency,
      vehiclePrice,
      isInCompare,
    };
  },
  methods: {
    addVehicleToCompare() {
      if (!this.vehicle) {
        console.warn("No vehicle loaded yet");
        return;
      }

      const listRef = getCompareCars();
      const alreadyIn = listRef.value.some((c) => c.id == this.vehicle.id);
      if (alreadyIn) {
        this.$router.push("/compare");
        return;
      }

      // Use the vehicle id from API directly
      const vehicleToCompare = {
        id: this.vehicle.id,
        brand: this.vehicle.specs.brand || this.vehicle.make,
        model: this.vehicle.specs.model || this.vehicle.model,
        year: this.vehicle.specs.year || this.vehicle.year,
        image: this.vehicle.image_url || this.placeholderImage,
        price: parseFloat(this.vehicle.specs.price || this.vehicle.rental),
        insurance: parseFloat(
          this.vehicle.specs.insurance || this.vehicle.insurance
        ),
        tracker: parseFloat(this.vehicle.specs.tracker || 200),
        specs: { ...this.vehicle.specs }, // keep all other specs
      };

      toggleCompare(vehicleToCompare); // Add to compare store
    },

    goToCompare() {
      this.$router.push("/compare");
    },
  },
};
</script>

<style scoped>
.vehicle-details-page {
  text-align: center;
}

@media (min-width: 1024px) {
  .vehicle-details-page {
    text-align: left;
  }
}
</style>
